import { createPortal } from "react-dom";

interface ConfirmationDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
}

export default function ConfirmationDialog({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText = "Yes, send message",
  cancelText = "Cancel",
  isLoading = false,
}: ConfirmationDialogProps) {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {title}
        </h3>
        {(() => {
          // Try to parse structured Subject/Content from the message, while preserving the leading prompt
          const subjectLineRegex = /Subject:\s*([^\r\n]*)/;
          const contentRegex = /Content:\s*([\s\S]*)$/;
          const subjectMatch = message.match(subjectLineRegex);
          const contentMatch = message.match(contentRegex);
          const hasStructured = !!contentMatch; // Content is always expected

          if (!hasStructured) {
            return (
              <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
            );
          }

          // Extract the leading prompt (everything before Subject/Content section)
          const splitPoint =
            message.indexOf("Subject:") !== -1
              ? message.indexOf("Subject:")
              : message.indexOf("Content:");
          const prompt =
            splitPoint > 0 ? message.slice(0, splitPoint).trim() : "";
          const subject = (subjectMatch?.[1] || "").trim();
          const content = (contentMatch?.[1] || "").trim();

          return (
            <div className="mb-6 text-gray-600 dark:text-gray-300">
              {prompt && <p className="mb-3">{prompt}</p>}
              {subject && (
                <p className="mb-1">
                  <span className="font-semibold">Subject:</span> {subject}
                </p>
              )}
              <p>
                <span className="font-semibold">Content:</span> {content}
              </p>
            </div>
          );
        })()}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-white bg-[#1100D8] rounded-md hover:bg-[#0e00b8] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Sending..." : confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
